import { authenticatedFetch } from "./apiClient";
import {
  GA4ConversionInsightGenerationMessageDto,
  GoogleAnalyticsAccountList,
  GoogleAnalyticsConnectionResponse,
  GoogleAnalyticsDisconnectAndPurgeResponse,
  GoogleAnalyticsDisconnectResponse,
  GoogleAnalyticsInsightsGenerationRequest,
  GoogleAnalyticsPropertiesResponse,
  GoogleAnalyticsStatusResponse,
} from "@/types/GoogleAnalytics";

const API_BASE_URL = import.meta.env.VITE_API_URL;
const GA4_CLOUD_FUNC_BASE_PATH = "/fn/integrations/google-analytics"; // goes to cloud function for a faster startup time
const GA4_CLOUD_RUN_BASE_PATH = "/v1/integrations/google-analytics"; // goes to core backend gcr worker

export class GoogleAnalyticsService {
  // Multi-account methods
  static async getAllAccounts(): Promise<GoogleAnalyticsAccountList> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GA4_CLOUD_RUN_BASE_PATH}/accounts`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch GA4 accounts: ${errorText}`);
    }

    return response.json();
  }

  static async connectGA4(
    redirectUrl?: string,
  ): Promise<GoogleAnalyticsConnectionResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GA4_CLOUD_FUNC_BASE_PATH}/connect`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          redirect_url: redirectUrl,
        }),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to initiate GA4 connection: ${errorText}`);
    }

    return response.json();
  }

  static async checkGA4Status(
    accountId: string,
  ): Promise<GoogleAnalyticsStatusResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GA4_CLOUD_RUN_BASE_PATH}/status?account_id=${accountId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to check GA4 status: ${errorText}`);
    }

    return response.json();
  }

  static async getGA4Properties(
    accountId: string,
  ): Promise<GoogleAnalyticsPropertiesResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GA4_CLOUD_RUN_BASE_PATH}/properties?account_id=${accountId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch GA4 properties: ${errorText}`);
    }

    return response.json();
  }


  static async disconnectGA4(
    accountId: string,
  ): Promise<GoogleAnalyticsDisconnectResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GA4_CLOUD_RUN_BASE_PATH}/disconnect?account_id=${accountId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to disconnect GA4: ${errorText}`);
    }

    return response.json();
  }

  static async disconnectAndPurgeGA4(
    accountId: string,
    domainId: string,
  ): Promise<GoogleAnalyticsDisconnectAndPurgeResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${GA4_CLOUD_RUN_BASE_PATH}/disconnect-and-purge?account_id=${accountId}&domain_id=${domainId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "Failed to disconnect and purge GA4");
    }

    return response.json();
  }

  static async generateInsights(
    domainId: string,
    pageId: string,
  ): Promise<GA4ConversionInsightGenerationMessageDto> {
    const insightsRequest: GoogleAnalyticsInsightsGenerationRequest = {
      domain_id: domainId,
      page_id: pageId,
    };

    const response = await authenticatedFetch(
      `${API_BASE_URL}/v1/generations/google-analytics-conversion-insights/generate-background`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(insightsRequest),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to generate GA4 conversion insights: ${errorText}`,
      );
    }

    return response.json();
  }


  // Helper method to parse URL parameters for OAuth callback
  static parseCallbackParams(url: string): {
    success?: boolean;
    error?: string;
  } {
    const urlObj = new URL(url);
    const success = urlObj.searchParams.get("success") === "true";
    const error = urlObj.searchParams.get("error");

    return { success, error: error || undefined };
  }
}
